import { useState, useMemo } from 'react';
import { Bell, Check, EllipsisVertical, MailCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationItem } from './notification-item';
import type { AppNotification } from '@socialflows/shared-lib';

type NotificationPanelProps = {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  onMarkAsRead: (ids: string[]) => Promise<void>;
  onMarkAllAsRead: () => Promise<void>;
};

type FilterTab = 'all' | 'orders' | 'social' | 'system';

const ORDER_TYPES = new Set([
  'ORDER_CREATED',
  'ORDER_STATUS_CHANGED',
  'ORDER_CANCELLED',
  'PAYMENT_FAILED',
  'DELIVERY_EXCEPTION',
]);

const SOCIAL_TYPES = new Set([
  'TICKET_CREATED',
  'TICKET_ASSIGNED',
  'POST_PUBLISHED',
  'POST_PUBLISH_FAILED',
  'REPLY_FAILED',
  'CHANNEL_DISCONNECTED',
]);

const SYSTEM_TYPES = new Set(['LOW_STOCK', 'OUT_OF_STOCK', 'WORKFLOW_FAILED']);

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'orders', label: 'Orders' },
  { value: 'social', label: 'Social' },
  { value: 'system', label: 'System' },
];

function getFilterForTab(tab: FilterTab): (n: AppNotification) => boolean {
  switch (tab) {
    case 'orders':
      return (n) => ORDER_TYPES.has(n.type);
    case 'social':
      return (n) => SOCIAL_TYPES.has(n.type);
    case 'system':
      return (n) => SYSTEM_TYPES.has(n.type);
    default:
      return () => true;
  }
}

function NotificationSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-3 px-4 py-3">
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="flex-1 flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-14 rounded" />
              <Skeleton className="h-2.5 w-16 ml-auto" />
            </div>
            <Skeleton className="h-3.5 w-4/5" />
            <Skeleton className="h-3 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function NotificationPanel({
  notifications,
  unreadCount,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationPanelProps) {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const filtered = useMemo(() => {
    let result = notifications.filter(getFilterForTab(activeTab));
    if (unreadOnly) {
      result = result.filter((n) => !n.isRead);
    }
    return result;
  }, [notifications, activeTab, unreadOnly]);

  const tabCounts = useMemo(() => {
    const counts: Record<FilterTab, { total: number; unread: number }> = {
      all: {
        total: notifications.length,
        unread: notifications.filter((n) => !n.isRead).length,
      },
      orders: {
        total: notifications.filter((n) => ORDER_TYPES.has(n.type)).length,
        unread: notifications.filter((n) => ORDER_TYPES.has(n.type) && !n.isRead).length,
      },
      social: {
        total: notifications.filter((n) => SOCIAL_TYPES.has(n.type)).length,
        unread: notifications.filter((n) => SOCIAL_TYPES.has(n.type) && !n.isRead).length,
      },
      system: {
        total: notifications.filter((n) => SYSTEM_TYPES.has(n.type)).length,
        unread: notifications.filter((n) => SYSTEM_TYPES.has(n.type) && !n.isRead).length,
      },
    };
    return counts;
  }, [notifications]);

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <div className="flex items-center gap-2">
          <Bell className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Notifications</h3>
        </div>
        <div className="flex items-center gap-1">
          <Toggle
            size="sm"
            pressed={unreadOnly}
            onPressedChange={setUnreadOnly}
            className="h-7 px-2 text-xs gap-1 data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
          >
            <MailCheck className="size-3" />
            Unread
          </Toggle>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-7">
                <EllipsisVertical className="size-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={onMarkAllAsRead} disabled={tabCounts.all.unread === 0}>
                <Check className="size-3.5 mr-2" />
                Mark all as read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="px-4 pb-2 shrink-0">
        <div className="flex gap-1">
          {FILTER_TABS.map((tab) => {
            const counts = tabCounts[tab.value];
            const showCount = counts.unread;
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'inline-flex items-center justify-center rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  activeTab === tab.value
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground'
                )}
              >
                {tab.label}
                {showCount > 0 && (
                  <span
                    className={cn(
                      'ml-1.5 flex size-4 items-center justify-center rounded-full text-[9px] font-semibold',
                      activeTab === tab.value
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {showCount > 99 ? '99+' : showCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <Separator className="shrink-0" />

      <ScrollArea
        className="flex-1 max-h-[420px] min-h-0 flex flex-col overflow-y-auto
"
      >
        {loading ? (
          <NotificationSkeleton />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <div className="flex size-12 items-center justify-center rounded-full bg-muted">
              <Bell className="size-5 opacity-60" />
            </div>
            <p className="mt-3 text-sm font-medium">
              {unreadOnly
                ? activeTab === 'all'
                  ? 'No unread notifications'
                  : `No unread ${activeTab} notifications`
                : activeTab === 'all'
                  ? 'No notifications'
                  : `No ${activeTab} notifications`}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">You're all caught up</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {filtered.map((notification, index) => (
              <div key={notification.id}>
                {index > 0 && <Separator />}
                <NotificationItem notification={notification} onRead={(id) => onMarkAsRead([id])} />
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
